import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { sortBy } from 'lodash';

import PickingListIcon from '../../../assets/icons/PickingListIcon';
import ChecklistsIcon from '../../../assets/icons/ChecklistsIcon';
import ShippingLabelsIcon from '../../../assets/icons/ShippingLabelsIcon';
import DispatchDocumentIcon from '../../../assets/icons/DispatchDocumentIcon';
import GtinIcon from '../../../assets/icons/GtinIcon';

import DispatchDocumentType from './DispatchDocumentType';
import DispatchChecklistsContainer from './DispatchChecklistsContainer';
import DispatchPickingListContainer from './DispatchPickingListContainer';
import DispatchNote from './DispatchNote';
import ColorButton from '../../shared/ColorButton';
import Modal from '../../shared/modal';

import { caseChecklistStatuses, setAllocationStatuses } from '../../../constants/enums';

const { AVAILABLE, ASSIGNED, SHIPPED, RETURNED, USAGE } = setAllocationStatuses;

export const dispatchDocumentTypes = {
  PICKING_LIST: 'PICKING_LIST',
  DISPATCH_DOCUMENT: 'DISPATCH_DOCUMENT',
  CHECKLISTS: 'CHECKLISTS',
  SHIPPING_LABELS: 'SHIPPING_LABELS',
  UDI_REFERENCE_DOCUMENT: 'UDI_REFERENCE_DOCUMENT',
}

const documentTypesList = [
  {
    id: dispatchDocumentTypes.PICKING_LIST,
    title: 'Picking List',
    subtitle: 'Kit & Item equipment list',
    icon: <PickingListIcon />,
  },
  {
    id: dispatchDocumentTypes.DISPATCH_DOCUMENT,
    title: 'Dispatch Document',
    subtitle: 'Shipping info for loan equipment',
    icon: <DispatchDocumentIcon />,
  },
  {
    id: dispatchDocumentTypes.CHECKLISTS,
    title: 'Checklists',
    subtitle: 'Outbound inspection documents',
    icon: <ChecklistsIcon />,
  },
  {
    id: dispatchDocumentTypes.SHIPPING_LABELS,
    title: 'Shipping Labels',
    subtitle: 'Printable labels for equipment',
    icon: <ShippingLabelsIcon />,
  },
  {
    id: dispatchDocumentTypes.UDI_REFERENCE_DOCUMENT,
    title: 'UDI Reference Document',
    subtitle: "Information on UDI's shipped",
    icon: <GtinIcon />,
  },
];

const DispatchModal = (props) => {
  const {
    onClose,
    loading,
    onSubmit,
    tenantColor,
    open,
    kitsList = [],
    itemsList = [],
    setsAllocation = [],
    caseChecklists = [],
  } = props;

  const [activeView, setActiveView] = useState(dispatchDocumentTypes.PICKING_LIST);
  const [selected, setSelected] = useState([]);

  // const [search, setSearch] = useState('');
  // const [selectedType, setSelectedType] = useState('');

  const [dispatchNote, setDispatchNote] = useState('');
  const [shippingNote, setShippingNote] = useState('');

  const [selectedChecklistKits, setSelectedChecklistKits] = useState([]);
  const [selectedChecklistItems, setSelectedChecklistItems] = useState([]);

  const [selectedPickingListKits, setSelectedPickingListKits] = useState([]);
  const [selectedPickingListItems, setSelectedPickingListItems] = useState([]);

  const checklistKits = useMemo(() => setsAllocation.filter((allocation) => {
    const _kit = kitsList.find((k) => k.id === allocation.kit);
    const checklist = caseChecklists.find((checklist) => checklist.allocationId === allocation.id);
    return !!allocation.kit && !_kit?.isConsignment && [ASSIGNED.value, SHIPPED.value, RETURNED.value, USAGE.value].includes(allocation.status) && [caseChecklistStatuses.COMPLETE.value, caseChecklistStatuses.INCOMPLETE.value].includes(checklist?.status);
  }), [kitsList, setsAllocation, caseChecklists]);
  const checklistItems = useMemo(() => setsAllocation.filter((allocation) => !!allocation.itemId && [ASSIGNED.value, SHIPPED.value, RETURNED.value, USAGE.value].includes(allocation.status)), [setsAllocation]);

  const pickingListKits = useMemo(() => setsAllocation.filter((allocation) => {
    const _kit = kitsList.find((k) => k.id === allocation.kit);
    return !!allocation.kit && !_kit?.isConsignment && [AVAILABLE.value, ASSIGNED.value].includes(allocation.status);
  }), [kitsList, setsAllocation, caseChecklists]);
  const pickingListItems = useMemo(() => setsAllocation.filter((allocation) => !!allocation.itemId && [AVAILABLE.value, ASSIGNED.value].includes(allocation.status)), [setsAllocation]);

  useEffect(() => {
    if (open && !!setsAllocation.length) {
      const _checklistKits = checklistKits.map((allocation) => allocation.id);
      const _checklistItems = checklistItems.map((allocation) => allocation.id);
      setSelectedChecklistKits(_checklistKits);
      setSelectedChecklistItems(_checklistItems);

      const _pickingListKits = pickingListKits.map((allocation) => allocation.id);
      const _pickingListItems = pickingListItems.map((allocation) => allocation.id);
      setSelectedPickingListKits(_pickingListKits);
      setSelectedPickingListItems(_pickingListItems);
    }
  }, [open, setsAllocation]);

  useEffect(() => {
    if (!open) {
      setSelected([]);
      setActiveView(dispatchDocumentTypes.PICKING_LIST);
      setDispatchNote('');
      setShippingNote('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!selected.length) {
      return;
    }

    const exportData = {
      selected,
      dispatchNote,
      shippingNote,
      documents: [],
      selectedChecklistKits,
      selectedChecklistItems,
      selectedPickingListKits,
      selectedPickingListItems,
    };
    onSubmit(exportData);
  };

  const onCheck = (id, checked) => {
    if (checked) {
      setSelected([...selected, id]);
    } else {
      setSelected([...selected].filter((type) => type !== id));
    }
  };

  // Checklist

  const onKitSelected = (id) => {
    if (selectedChecklistKits.includes(id)) {
      setSelectedChecklistKits(selectedChecklistKits.filter((kitId) => kitId !== id));
    } else {
      setSelectedChecklistKits([...selectedChecklistKits, id]);
    }
  };

  const onItemSelected = (id) => {
    if (selectedChecklistItems.includes(id)) {
      setSelectedChecklistItems(selectedChecklistItems.filter((itemId) => itemId !== id));
    } else {
      setSelectedChecklistItems([...selectedChecklistItems, id]);
    }
  };

  const onSelectAllKits = () => {
    const _kits = checklistKits.map((allocation) => allocation.id);
    if (_kits.some((kitId) => !selectedChecklistKits.includes(kitId))) {
      setSelectedChecklistKits(_kits);
    } else {
      setSelectedChecklistKits([]);
    }
  };

  const onSelectAllItems = () => {
    const _items = checklistItems.map((allocation) => allocation.id);
    if (_items.some((itemId) => !selectedChecklistItems.includes(itemId))) {
      setSelectedChecklistItems(_items);
    } else {
      setSelectedChecklistItems([]);
    }
  };

  // Picking List

  const onPickingListKitSelected = (id) => {
    if (selectedPickingListKits.includes(id)) {
      setSelectedPickingListKits(selectedPickingListKits.filter((kitId) => kitId !== id));
    } else {
      setSelectedPickingListKits([...selectedPickingListKits, id]);
    }
  };

  const onPickingListItemSelected = (id) => {
    if (selectedPickingListItems.includes(id)) {
      setSelectedPickingListItems(selectedPickingListItems.filter((itemId) => itemId !== id));
    } else {
      setSelectedPickingListItems([...selectedPickingListItems, id]);
    }
  };

  const onSelectAllPickingListKits = () => {
    const _kits = pickingListKits.map((allocation) => allocation.id);
    if (_kits.some((kitId) => !selectedPickingListKits.includes(kitId))) {
      setSelectedPickingListKits(_kits);
    } else {
      setSelectedPickingListKits([]);
    }
  };

  const onSelectAllPickingListItems = () => {
    const _items = pickingListItems.map((allocation) => allocation.id);
    if (_items.some((itemId) => !selectedPickingListItems.includes(itemId))) {
      setSelectedPickingListItems(_items);
    } else {
      setSelectedPickingListItems([]);
    }
  };

  const getKitId = useCallback((kitId) => {
    const kit = kitsList.find((item) => item.id === kitId);
    return kit ? kit.kitId : '';
  }, [setsAllocation, kitsList]);

  const getKitName = useCallback((kitId) => {
    const kit = kitsList.find((item) => item.id === kitId);
    return kit ? kit.name : '';
  }, [setsAllocation, kitsList]);

  const getKitType = useCallback((kitId) => {
    const kit = kitsList.find((item) => item.id === kitId);
    return kit ? kit.type : '';
  }, [setsAllocation, kitsList]);

  const getItemCode = useCallback((itemId) => {
    const item = itemsList.find((item) => item.id === itemId);
    return item ? item.code : '';
  }, [setsAllocation, itemsList]);

  const getItemDescription = useCallback((itemId) => {
    const item = itemsList.find((item) => item.id === itemId);
    return item ? (item.checklistDescription || item.description) : '';
  }, [setsAllocation, itemsList]);

  const getItemType = useCallback((itemId) => {
    const item = itemsList.find((item) => item.id === itemId);
    return item ? item.type : '';
  }, [setsAllocation, itemsList]);

  const renderView = () => {
    switch (activeView) {
      case dispatchDocumentTypes.PICKING_LIST:
        const _pickingListKits = pickingListKits
          .map((allocation) => ({
            id: allocation.id,
            kitId: getKitId(allocation.kit),
            kitName: getKitName(allocation.kit),
            type: getKitType(allocation.kit),
            status: allocation.status,
          }));

        const _pickingListItems = pickingListItems
          .map((allocation) => ({
            id: allocation.id,
            code: getItemCode(allocation.itemId),
            description: getItemDescription(allocation.itemId),
            type: getItemType(allocation.itemId),
            status: allocation.status,
          }));

        return (
          <DispatchPickingListContainer
            kits={sortBy(_pickingListKits, 'kitId')}
            items={sortBy(_pickingListItems, 'code')}
            selectedKits={selectedPickingListKits}
            selectedItems={selectedPickingListItems}
            onKitSelected={onPickingListKitSelected}
            onItemSelected={onPickingListItemSelected}
            onSelectAllKits={onSelectAllPickingListKits}
            onSelectAllItems={onSelectAllPickingListItems}
          />
        );
      case dispatchDocumentTypes.DISPATCH_DOCUMENT:
        return <DispatchNote value={dispatchNote} onChange={setDispatchNote} title='Dispatch Note' />;
      case dispatchDocumentTypes.CHECKLISTS:
      case dispatchDocumentTypes.UDI_REFERENCE_DOCUMENT:
        const _checklistKits = checklistKits
          .map((allocation) => ({
            id: allocation.id,
            kitId: getKitId(allocation.kit),
            kitName: getKitName(allocation.kit),
            type: getKitType(allocation.kit),
            status: allocation.status,
          }));

        const _checklistItems = checklistItems
          .map((allocation) => ({
            id: allocation.id,
            code: getItemCode(allocation.itemId),
            description: getItemDescription(allocation.itemId),
            type: getItemType(allocation.itemId),
            status: allocation.status,
          }));

        return (
          <DispatchChecklistsContainer
            kits={sortBy(_checklistKits, 'kitId')}
            items={sortBy(_checklistItems, 'code')}
            selectedKits={selectedChecklistKits}
            selectedItems={selectedChecklistItems}
            onKitSelected={onKitSelected}
            onItemSelected={onItemSelected}
            onSelectAllKits={onSelectAllKits}
            onSelectAllItems={onSelectAllItems}
          />
        );
      case dispatchDocumentTypes.SHIPPING_LABELS:
        return <DispatchNote value={shippingNote} onChange={setShippingNote} title='Shipping Note' />;
      default:
        return null;
    }
  };

  const isDisabled = (item) => {
    if ([dispatchDocumentTypes.CHECKLISTS, dispatchDocumentTypes.UDI_REFERENCE_DOCUMENT].includes(item.id)) {
      return ![...checklistKits, ...checklistItems].length;
    }
    if (item.id === dispatchDocumentTypes.DISPATCH_DOCUMENT) {
      return setsAllocation.every((allocation) => !allocation.trackingNumber);
    }

    return false;
  };

  // const isDisabled = (item) => {
  //   return (
  //     [dispatchDocumentTypes.CHECKLISTS, dispatchDocumentTypes.DISPATCH_DOCUMENT].includes(item.id)
  //     && (
  //       setsAllocation.every((allocation) => ![ASSIGNED.value, SHIPPED.value, RETURNED.value, USAGE.value].includes(allocation.status))
  //       || (
  //         setsAllocation?.every((allocation) => !allocation.trackingNumber) &&
  //         caseChecklists?.every((checklist) => ![caseChecklistStatuses.COMPLETE.value, caseChecklistStatuses.INCOMPLETE.value]?.includes(checklist?.status))
  //       )
  //     )
  //   );
  // };

  return (
    <Modal
      open={open}
      onClose={onClose}
      fullWidth
      size="lg"
    >
      <div className="dispatch-modal__container">
        <div className="dispatch-modal__title-container">
          <div>
            <div className="font-bold font-size-bg">Dispatch Documents</div>
            <div className="m-t-sm font-size-sm">Export shipping documentation</div>
          </div>
          <div className="d-flex">
            {/*<Input*/}
            {/*  type="search"*/}
            {/*  placeholder="Search item code or description"*/}
            {/*  value={search}*/}
            {/*  onChange={(e) => setSearch(e.target.value)}*/}
            {/*  backgroundColor="#fafafa"*/}
            {/*/>*/}
            {/*<Select*/}
            {/*  value={selectedType}*/}
            {/*  onChange={setSelectedType}*/}
            {/*  options={[{ label: 'All', value: '' }, ...Object.values(itemTypeOptions)]}*/}
            {/*  endAdornment="Select Type"*/}
            {/*  backgroundColor="#fafafa"*/}
            {/*/>*/}
          </div>
        </div>
        <div className="dispatch-modal__content">
          <div className="dispatch-modal__list">
            {documentTypesList.map((item) => (
              <DispatchDocumentType
                key={item.id}
                type={item.id}
                title={item.title}
                subtitle={item.subtitle}
                icon={item.icon}
                checked={selected.includes(item.id)}
                active={activeView === item.id}
                onClick={() => setActiveView(item.id)}
                onCheck={(checked) => onCheck(item.id, checked)}
                tenantColor={tenantColor}
                disabled={isDisabled(item)}
              />
            ))}
          </div>
          <div className="dispatch-modal__main" style={{ minHeight: 400 }}>
            {renderView()}
          </div>
        </div>
        <div className="dispatch-modal__actions">
          <ColorButton
            text="Close"
            color="blue"
            onClick={onClose}
            height={30}
          />

          <ColorButton
            text="Export"
            color="blue"
            onClick={handleSubmit}
            loading={loading}
            disabled={loading || !selected.length}
            height={30}
          />
        </div>
      </div>
    </Modal>
  );
};

export default DispatchModal;
